/**
 * Pure helpers narrowing a 3D file's opaque `metadata` / `attachmentUrls` JSON scalars
 * into the interchange exports stored beside it.
 *
 * The file itself is GLB — that is what the viewer and the preview renderer read.
 * Formats GLB cannot represent are kept as attachments: FBX carries rigs and animation
 * for engine import, OBJ carries quad topology (glTF primitives are triangles only).
 * The backend records each one on `FileMetadata.mesh_formats`.
 */

export type MeshFormat = "fbx" | "obj";

const MESH_FORMATS: MeshFormat[] = ["fbx", "obj"];

export interface MeshFormatSource {
  metadata?: unknown;
  attachmentUrls?: unknown;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** Attachment name recorded for a format on this file, or null when it has no such export. */
const attachmentNameFor = (
  metadata: unknown,
  format: MeshFormat,
): string | null => {
  const meshFormats = asRecord(metadata)?.meshFormats;
  if (!Array.isArray(meshFormats)) return null;
  for (const raw of meshFormats) {
    const entry = asRecord(raw);
    if (entry?.format === format && typeof entry.attachmentName === "string") {
      return entry.attachmentName;
    }
  }
  return null;
};

/** Signed URL of a file's stored export in this format, or null when it has none. */
export const meshFormatUrl = (
  file: MeshFormatSource,
  format: MeshFormat,
): string | null => {
  const attachmentName = attachmentNameFor(file.metadata, format);
  if (!attachmentName) return null;
  const url = asRecord(file.attachmentUrls)?.[attachmentName];
  return typeof url === "string" ? url : null;
};

/** Formats this file has a stored export for, in a stable order. */
export const availableMeshFormats = (file: MeshFormatSource): MeshFormat[] =>
  MESH_FORMATS.filter((format) => meshFormatUrl(file, format) !== null);

/** Download filename for an export: the file's own name with the format's extension. */
export const meshFormatFileName = (
  fileName: string,
  format: MeshFormat,
): string => {
  const base = fileName.includes(".")
    ? fileName.slice(0, fileName.lastIndexOf("."))
    : fileName;
  return `${base}.${format}`;
};
